import { useState, useCallback } from 'react';
import { OptionType } from '@/types';
import type { MenuOption } from '@/types';
import { normalizeSelectedOptions } from '@/utils/selectedOptions';

// 선택된 옵션 상태 타입
export type SelectedOptionsState = Record<string, string[]>;

// 유효성 검사 에러 타입
export type ValidationErrorsState = Record<string, string>;

/**
 * #493: 입력형 옵션 (number_input/text_input/textarea) 사용자 입력 값.
 * key = optionGroup.id, value = 사용자 입력 문자열.
 * number_input 도 string 으로 보존 (빈 입력 vs 0 구분).
 */
export type InputValuesState = Record<string, string>;

// 훅 Props 타입
export interface UseMenuOptionStateProps {
  menuOptions: MenuOption[];
}

// 훅 반환 타입
export interface UseMenuOptionStateReturn {
  // 상태
  selectedOptions: SelectedOptionsState;
  validationErrors: ValidationErrorsState;
  // #493: 입력형 옵션 사용자 입력 값
  inputValues: InputValuesState;

  // 상태 업데이트 함수
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptionsState>>;
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrorsState>>;

  // 핸들러 함수
  handleOptionSelect: (optionGroup: MenuOption, optionId: string) => void;
  handleOptionDecrement: (optionGroup: MenuOption, optionId: string) => void;
  // #493: 입력형 옵션 값 변경 핸들러
  handleInputChange: (optionGroupId: string, value: string) => void;

  // 유효성 검사
  validateOptions: (translations: {
    pleaseSelectRequired: string;
    pleaseSelectMinimum: string;
    pleaseSelectExactly: string;
    pleaseSelectRange: string;
  }) => boolean;

  // 초기화 함수
  resetOptions: () => void;
  initializeOptions: (
    initialSelectedOptions?: SelectedOptionsState,
    initialInputValues?: InputValuesState,
  ) => void;
}

export function useMenuOptionState({ menuOptions }: UseMenuOptionStateProps): UseMenuOptionStateReturn {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsState>({});
  const [validationErrors, setValidationErrors] = useState<ValidationErrorsState>({});
  // #493: 입력형 옵션 사용자 입력 값 state
  const [inputValues, setInputValues] = useState<InputValuesState>({});

  // 초기화 함수
  const resetOptions = useCallback(() => {
    setSelectedOptions({});
    setValidationErrors({});
    setInputValues({});
  }, []);

  // 초기값 설정 함수 - 항상 이전 상태를 완전히 교체하도록 보장
  const initializeOptions = useCallback((
    initialSelectedOptions?: SelectedOptionsState,
    initialInputValues?: InputValuesState,
  ) => {
    setValidationErrors({});
    const safe = normalizeSelectedOptions(initialSelectedOptions);
    setSelectedOptions(safe);
    const safeInputs = initialInputValues && Object.keys(initialInputValues).length > 0
      ? { ...initialInputValues }
      : {};
    setInputValues(safeInputs);
  }, []);

  // #493: 입력형 옵션 값 변경 핸들러
  const handleInputChange = useCallback((optionGroupId: string, value: string) => {
    setInputValues(prev => ({ ...prev, [optionGroupId]: value }));
    // 입력 시점 에러 클리어
    setValidationErrors(prev => {
      if (prev[optionGroupId]) {
        const newErrors = { ...prev };
        delete newErrors[optionGroupId];
        return newErrors;
      }
      return prev;
    });
  }, []);

  // 일반 옵션 선택 처리 (수량 제어가 아닌 경우)
  const handleOptionSelect = useCallback((optionGroup: MenuOption, optionId: string) => {
    setSelectedOptions(prev => {
      const currentSelections = prev[optionGroup.id] || [];
      let newSelections: string[];

      switch (optionGroup.type) {
        case OptionType.REQUIRED_SINGLE:
          // 필수 단일 선택: 항상 새로운 선택으로 교체
          newSelections = [optionId];
          break;

        case OptionType.OPTIONAL_SINGLE:
          // 선택적 단일 선택: 이미 선택된 경우 해제, 아니면 선택
          if (currentSelections.includes(optionId)) {
            newSelections = [];
          } else {
            newSelections = [optionId];
          }
          break;

        case OptionType.MULTI_UNLIMITED:
        case OptionType.MULTI_EXACT:
        case OptionType.MULTI_RANGE: {
          const option = optionGroup.options.find(item => item.id === optionId);
          const selectedQuantity = currentSelections.filter(id => id === optionId).length;
          const quantityLimit = option?.quantityLimit ?? Infinity;

          if (
            currentSelections.length < (optionGroup.maxSelection || 999)
            && selectedQuantity < quantityLimit
          ) {
            newSelections = [...currentSelections, optionId];
          } else {
            newSelections = currentSelections;
          }
          break;
        }

        default:
          newSelections = currentSelections;
      }

      return {
        ...prev,
        [optionGroup.id]: newSelections
      };
    });

    // 에러 메시지 제거
    setValidationErrors(prev => {
      if (prev[optionGroup.id]) {
        const newErrors = { ...prev };
        delete newErrors[optionGroup.id];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const handleOptionDecrement = useCallback((optionGroup: MenuOption, optionId: string) => {
    setSelectedOptions(prev => {
      const currentSelections = prev[optionGroup.id] || [];
      const removeIndex = currentSelections.lastIndexOf(optionId);
      if (removeIndex < 0) {
        return prev;
      }

      const newSelections = [
        ...currentSelections.slice(0, removeIndex),
        ...currentSelections.slice(removeIndex + 1),
      ];

      return {
        ...prev,
        [optionGroup.id]: newSelections,
      };
    });

    setValidationErrors(prev => {
      if (prev[optionGroup.id]) {
        const newErrors = { ...prev };
        delete newErrors[optionGroup.id];
        return newErrors;
      }
      return prev;
    });
  }, []);

  // 옵션 검증
  const validateOptions = useCallback((translations: {
    pleaseSelectRequired: string;
    pleaseSelectMinimum: string;
    pleaseSelectExactly: string;
    pleaseSelectRange: string;
  }): boolean => {
    const errors: ValidationErrorsState = {};
    let isValid = true;

    menuOptions.forEach(optionGroup => {
      const selections = selectedOptions[optionGroup.id] || [];
      const selectionCount = selections.length;
      const minSelection = optionGroup.minSelection || 0;
      const maxSelection = optionGroup.maxSelection || 999;

      switch (optionGroup.type) {
        case OptionType.REQUIRED_SINGLE:
          if (selectionCount === 0) {
            errors[optionGroup.id] = translations.pleaseSelectRequired;
            isValid = false;
          }
          break;

        case OptionType.OPTIONAL_SINGLE:
          // 선택적이므로 검증 불필요
          break;

        case OptionType.MULTI_UNLIMITED:
          // 무제한이므로 최소값만 검증
          if (minSelection > 0 && selectionCount < minSelection) {
            errors[optionGroup.id] = translations.pleaseSelectMinimum.replace('{count}', minSelection.toString());
            isValid = false;
          }
          break;

        case OptionType.MULTI_EXACT: {
          const exactCount = optionGroup.exactSelection || minSelection;
          if (selectionCount !== exactCount) {
            errors[optionGroup.id] = translations.pleaseSelectExactly.replace('{count}', exactCount.toString());
            isValid = false;
          }
          break;
        }

        case OptionType.MULTI_RANGE:
          if (selectionCount < minSelection || selectionCount > maxSelection) {
            errors[optionGroup.id] = translations.pleaseSelectRange
              .replace('{min}', minSelection.toString())
              .replace('{max}', maxSelection.toString());
            isValid = false;
          }
          break;

      }
    });

    setValidationErrors(errors);
    return isValid;
  }, [menuOptions, selectedOptions]);

  return {
    selectedOptions,
    validationErrors,
    inputValues,
    setSelectedOptions,
    setValidationErrors,
    handleOptionSelect,
    handleOptionDecrement,
    handleInputChange,
    validateOptions,
    resetOptions,
    initializeOptions
  };
}
